import { type ReactNode } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { MemberNameWithScar } from '@/components/ui/member-name-with-scar';
import { RoleBadge } from '@/components/ui/role-badge';
import { useMemberDisplay } from '@/hooks/use-member-display';
import { pbFileUrl } from '@/utils/pb-file-url';
import type { EnrichedMember } from '../types';
import { AccountStatusBadge } from './account-status-action';
import { CollectorStats } from './collector-stats-cells';
import { RowActions } from './row-actions';
import { VacationBadge, RedemptionChips } from './approacher-row-chips';

const TD = 'px-md py-sm';
const TR =
  'cursor-pointer border-b border-white/[0.03] transition-colors duration-150 hover:bg-white/[0.05]';

function UserCell({ a }: { a: EnrichedMember }): ReactNode {
  const src = a.photo ? pbFileUrl(a, a.photo) : undefined;
  const display = useMemberDisplay(a);
  const redeemed = !!a.has_used_redemption && !!a.redemption_passed_at;
  const label = display.handle ? `@${display.handle}` : display.name;
  return (
    <div className="flex items-center gap-sm">
      <Avatar src={src} name={display.handle ?? display.name} size="sm" />
      <MemberNameWithScar
        name={
          <span className="inline-flex items-center gap-1 text-[13px] text-text-primary">
            <span>{label}</span>
            {display.personalHandle && (
              <Badge variant="default">Personal</Badge>
            )}
          </span>
        }
        hasRedeemed={redeemed}
      />
    </div>
  );
}

function EmptyCells(): ReactNode {
  return (
    <>
      <td className={TD} />
      <td className={TD} />
      <td className={TD} />
    </>
  );
}

function NumberCell({ n }: { n: number | undefined }): ReactNode {
  return (
    <td className={`${TD} font-mono text-[11px] text-text-tertiary`}>
      {n ? `#${n}` : '—'}
    </td>
  );
}

function RowCells({ a }: { a: EnrichedMember }): ReactNode {
  return (
    <>
      <NumberCell n={a.player_number} />
      <td className={TD}>
        <UserCell a={a} />
      </td>
      <td className={TD}>
        <RoleBadge role={a.role} />
      </td>
      <td className={TD}>
        <AccountStatusBadge status={a.account_status} />
        <VacationBadge a={a} />
        <RedemptionChips a={a} />
      </td>
      {a.role === 'collector' ? <CollectorStats m={a} /> : <EmptyCells />}
      <td className={TD} onClick={(e) => e.stopPropagation()}>
        <RowActions member={a} />
      </td>
    </>
  );
}

type Props = { member: EnrichedMember; onClick: () => void };

export function ApproacherRow({ member: a, onClick }: Props): ReactNode {
  return (
    <tr className={TR} onClick={onClick}>
      <RowCells a={a} />
    </tr>
  );
}
